import React, { FC, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  HomeIcon,
  AnnotationIcon,
  PhotographIcon,
  PencilAltIcon,
  BookOpenIcon,
  QuestionMarkCircleIcon,
  ShoppingCartIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  AcademicCapIcon,
  UserCircleIcon,
  CreditCardIcon,
  CogIcon,
  ExternalLinkIcon,
  CashIcon,
  ChatIcon,
} from "@heroicons/react/outline";
import { UserIcon } from "@heroicons/react/solid";
import { useLocation, NavLink } from "react-router-dom";
// import SwipeableDrawer from '@bit/mui-org.material-ui.swipeable-drawer'
import { ReactComponent as StoryIcon } from "../../icons/story-icon.svg";
import ROUTES from "../../utils/routes";
import useWindowHeight from "../../utils/useWindowHeight";
import useSegment from "../../utils/useSegment";
import { AppState } from "../../../redux/root-reducer";
import { CartState } from "../../../redux/shopping-cart/types";
import { userStore } from "../../utils/UserContext";
import { Logo, Header } from "../Header";
import { Avatar } from "../../System";
import { IconIntercomOutline } from "../../Icon/Brands";
import { SplitContext } from "../../utils/SplitContext";
import { StyledSideBar, SideBarLink, SidebarPrompt } from ".";
import { useIsDesktop } from "../../utils/useIsDesktop";

const SideBar: FC = () => {
  const { user, loaded } = useContext(userStore);
  const { track } = useSegment();
  const { cartItems } = useSelector<AppState, CartState>(
    (state) => state.shoppingCart
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const { isDesktop } = useIsDesktop();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const windowHeight = useWindowHeight();
  const location = useLocation();
  const {
    isReady,
    isEnabled,
    treatments: { stories, community },
  } = useContext(SplitContext);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  return (
    <>
      {!isDesktop && (
        <Header toggleMobileNav={() => setSidebarOpen(!sidebarOpen)} />
      )}
      {/* <SwipeableDrawer
        anchor="left"
        open={isDesktop || sidebarOpen}
        variant={isDesktop ? 'persistent' : 'temporary'}
        onClose={() => setSidebarOpen(false)}
        onOpen={() => setSidebarOpen(true)}
        transitionDuration={isDesktop ? 0 : 200}
        disableDiscovery
        disableSwipeToOpen
      > */}
      <StyledSideBar windowHeight={windowHeight} loggedIn={!!user}>
        <div className="sidebar-top">
          <div className="sidebar-logo">
            <Logo />
          </div>
          <div className="sidebar-links">
            <SideBarLink
              id="home-tab"
              url={ROUTES.dashboard.home}
              icon={<HomeIcon />}
              isExact
            >
              Dashboard
            </SideBarLink>
            <SideBarLink
              id="drafts-tab"
              url={ROUTES.dashboard.drafts}
              icon={<PencilAltIcon />}
            >
              My Posts
            </SideBarLink>
            <div className="sidebar-link-group">
              <label>RESOURCES</label>
              <SideBarLink
                id="captions-tab"
                url={ROUTES.captions}
                icon={<AnnotationIcon />}
              >
                Captions
              </SideBarLink>
              {isReady && isEnabled(stories) && (
                <SideBarLink
                  id="stories-tab"
                  url={ROUTES.stories.list}
                  icon={<StoryIcon />}
                >
                  Stories
                </SideBarLink>
              )}
              <SideBarLink
                id="gallery-tab"
                url={ROUTES.gallery.main}
                icon={<PhotographIcon />}
              >
                Gallery
              </SideBarLink>
            </div>
            <div className="sidebar-link-group">
              <label>EDUCATION</label>
              <SideBarLink
                id="issues-tab"
                url={ROUTES.dashboard.issues}
                icon={<BookOpenIcon />}
              >
                Action Plans
              </SideBarLink>
              <SideBarLink
                id="courses-tab"
                url={ROUTES.dashboard.courses}
                icon={<AcademicCapIcon />}
              >
                Courses
              </SideBarLink>
            </div>
          </div>
        </div>
        <div className="sidebar-bottom">
          <div className="sidebar-links">
            {isReady && isEnabled(community) && (
              <SideBarLink
                id="community-tab"
                url={ROUTES.dashboard.community}
                icon={<ChatIcon />}
              >
                Community
              </SideBarLink>
            )}
            <SideBarLink
              url={ROUTES.external.helpCenterUrl}
              onClick={() => track("Viewed Resource Center")}
              target="_blank"
              icon={<QuestionMarkCircleIcon />}
            >
              Resource Center
            </SideBarLink>
            <SideBarLink
              url={ROUTES.external.supportMailing}
              id="intercom_btn"
              target="_blank"
              icon={<IconIntercomOutline size={24} />}
            >
              Chat Support
            </SideBarLink>
            <SideBarLink
              notification={(cartItems || []).length}
              url={ROUTES.checkout}
              icon={<ShoppingCartIcon />}
            >
              Cart
            </SideBarLink>
          </div>
          <div className="sidebar-menu">
            {menuOpen && (
              <div className="sidebar-user-links">
                <NavLink to={ROUTES.user.settings.profile}>
                  <UserCircleIcon />
                  Account Settings
                </NavLink>
                <NavLink to={ROUTES.affiliates.dashboard}>
                  <CashIcon />
                  {user?.payPalToken
                    ? "Affiliate Dashboard"
                    : "Become an Affiliate"}
                </NavLink>
                <NavLink to={ROUTES.user.settings.billing}>
                  <CreditCardIcon />
                  Billing
                </NavLink>
                {user?.role === "admin" && (
                  <NavLink to={ROUTES.admin.dashboard}>
                    <CogIcon />
                    Admin Panel
                  </NavLink>
                )}
                <NavLink to={ROUTES.user.logout}>
                  <ExternalLinkIcon />
                  Log Out
                </NavLink>
              </div>
            )}
            <button
              className="sidebar-user-container"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {loaded && user && (
                <div className="sidebar-user">
                  <Avatar name={user.displayName} />
                  <span className="sidebar-user-name">{user?.displayName}</span>
                </div>
              )}
              {!user && (
                <div className="sidebar-user">
                  <div className="sidebar-user-avatar avatar">
                    <UserIcon />
                  </div>
                </div>
              )}
              {menuOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </button>
          </div>
        </div>
        {loaded && !user && <SidebarPrompt />}
      </StyledSideBar>
      {/* </SwipeableDrawer> */}
    </>
  );
};

export default SideBar;
